import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seo from './pages.json';

// Updates an existing head tag (or creates it) so we never end up with duplicates
// of the static tags that ship in index.html.
const setTag = (selector, create, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const meta = (key, value, by = 'name') =>
  setTag(
    `meta[${by}="${key}"]`,
    () => {
      const m = document.createElement('meta');
      m.setAttribute(by, key);
      return m;
    },
    'content',
    value
  );

// For pages whose text is only known after loading (e.g. a blog post).
export const setPageMeta = (title, description) => {
  document.title = title;
  meta('description', description);
  meta('og:title', title, 'property');
  meta('og:description', description, 'property');
  meta('twitter:title', title);
  meta('twitter:description', description);
};

// Keeps title, description, canonical and social tags in sync with the current route.
// The build step (scripts/seo-pages.js) writes the same values into each page's HTML,
// so crawlers that don't run JavaScript see them too.
const RouteMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const page =
      seo.pages[path] ||
      (path.startsWith('/project/') ? seo.projectDetail : path.startsWith('/blog/') ? seo.blogDetail : null);
    const { title, description } = page || seo.notFound;
    const url = `${seo.siteUrl}${path === '/' ? '/' : path}`;

    setPageMeta(title, description);
    meta('robots', page ? 'index, follow, max-image-preview:large' : 'noindex, follow');
    meta('og:url', url, 'property');
    setTag(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      },
      'href',
      url
    );
  }, [pathname]);

  return null;
};

export default RouteMeta;
